import React from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import updateImageListSrc from "@utils/update-image-list-src";
import { BASE_IMG_URL, livePhotoImage } from '@images/imageUrls';

// 添加播放图标组件
const PlayIcon = () => (
  <>
    <img src={`${BASE_IMG_URL}/common/play.png`} alt="" className={styles.playIcon}/>
  </>
);

interface ImageItem {
  id: number;
  url: string;
  webpUrl?: string;
  width: number;
  height: number;
  fileWidth: number;
  fileHeight: number;
  thumbUrl?: string;
}

interface Cover {
  id: number;
  url: string;
  webpUrl?: string;
  width: number;
  height: number;
  fileWidth: number;
  fileHeight: number;
  thumbUrl?: string;
}

interface VideoData {
  videoCoverUrl?: string;
  videoCoverWebpUrl?: string;
  videoWidth?: number;
  videoHeight?: number;
  fileWidth: number;
  fileHeight: number;
  [key: string]: any;
}

interface PCThreadImgListProps {
  cover?: Cover | null;
  imageList: ImageItem[];
  maxDisplay?: number;
  topicList?: [];
  onImageClick?: (index: number, images: ImageItem[]) => void;
  isVideo?: boolean;
  videoData?: VideoData;
}

// 根据图片比例选择帖子列表页对应的 OSS 裁剪规则。
const getImageProcessType = (item: { fileWidth?: number; fileHeight?: number; width?: number; height?: number } | null | undefined): number => {
  if (!item) return 17;

  const width = item.fileWidth || item.width || 0;
  const height = item.fileHeight || item.height || 0;
  const ratio = height ? width / height : 1;

  if (Math.abs(ratio - 2) < 0.1) return 19;
  if (ratio < 1) return 18;
  return 17;
};

// 辅助函数：使用原图地址拼接帖子列表页 OSS 参数。
const getImageUrl = (item: { url?: string; fileWidth?: number; fileHeight?: number; width?: number; height?: number } | null | undefined): string => {
  if (!item) return '';

  return updateImageListSrc(item.url || '', getImageProcessType(item));
};

const PCThreadImgList: React.FC<PCThreadImgListProps> = ({
  cover,
  imageList = [],
  maxDisplay = 6,
  topicList = [],
  onImageClick,
  isVideo = false,
  videoData
}) => {
  // 处理图片加载错误
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>, type?: number) => {
    const imgElement = event.currentTarget as HTMLImageElement;
    imgElement.src = type == 1 
      ? `${BASE_IMG_URL}/common/pic_place1.png` 
      : `${BASE_IMG_URL}/common/pic_place2.png`;
    // 防止默认图也加载失败时陷入死循环
    imgElement.onerror = null;
  };
  
  // 合并 cover 和 videoData，优先使用 cover
  const mergedCover = (() => {
    if (cover && (cover.webpUrl || cover.url)) {
      const result = {
        id: cover.id || -1,
        url: cover.url,
        webpUrl: cover.webpUrl,
        fileWidth: cover.fileWidth || cover.width || 0,
        fileHeight: cover.fileHeight || cover.height || 0,
        width: cover.width || cover.fileWidth || 0,
        height: cover.height || cover.fileHeight || 0,
        thumbUrl: cover.thumbUrl || cover.url
      };
      return result;
    }
    
    // 如果 cover 不存在但 videoData 存在，使用 videoData
    if (videoData && videoData.videoCoverUrl) {
      const result = {
        id: -1,
        url: videoData.videoCoverUrl,
        webpUrl: videoData.videoCoverWebpUrl,
        fileWidth: videoData.fileWidth || videoData.videoWidth || 0,
        fileHeight: videoData.fileHeight || videoData.videoHeight || 0,
        width: videoData.fileWidth || videoData.videoWidth || 0,
        height: videoData.fileHeight || videoData.videoHeight || 0,
        thumbUrl: videoData.videoCoverUrl
      };
      return result;
    }
    
    return null;
  })();
  
  // 判断封面图是否为2:1比例
  const isCover2to1 = (): boolean => {
    if (!mergedCover) return false;
    const ratio = mergedCover.fileWidth / mergedCover.fileHeight;
    return Math.abs(ratio - 2) < 0.1;
  };

  // 获取所有图片的统一比例类名（用于网格布局）
  const getAspectRatioClass = (): string => {
    if (mergedCover && !isCover2to1()) {
      return getImageProcessType(mergedCover) === 18 ? 'aspect-2-3' : 'aspect-1-1';
    }
    
    if (imageList.length > 0) {
      return getImageProcessType(imageList[0]) === 18 ? 'aspect-2-3' : 'aspect-1-1';
    }
    
    return 'aspect-1-1';
  };

  // 处理图片点击
  const handleImageClick = (index: number) => {
    if (onImageClick) {
      const allImages = mergedCover ? 
        [{ 
          id: mergedCover.id, 
          url: mergedCover.url, 
          webpUrl: mergedCover.webpUrl,
          fileWidth: mergedCover.fileWidth, 
          fileHeight: mergedCover.fileHeight, 
          width: mergedCover.width,
          height: mergedCover.height,
          thumbUrl: mergedCover.thumbUrl
        }, ...imageList] : 
        imageList;
      onImageClick(index, allImages);
    }
  };

  // 渲染视频封面图（带播放图标）- 用于网格中的视频项
  const renderVideoCover = (coverItem: typeof mergedCover, onClick: () => void) => {
    const imageUrl = getImageUrl(coverItem);
    
    return (
      <div className={styles.videoCoverContainer}>
        <img
          src={imageUrl}
          loading="lazy"
          decoding="async"
          alt="视频封面"
          className={styles.postImage}
          onClick={onClick}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
        <PlayIcon />
      </div>
    );
  };

  // 渲染单张封面图模式（只要封面图是2:1比例就渲染）
  const renderSingleCover = () => {
    if (!mergedCover || !isCover2to1()) {
      return null;
    }
    
    const imageUrl = getImageUrl(mergedCover);
    
    return (
      <div className={classNames(styles.singleCover)}>
        <div className={classNames(styles['aspect-2-1'])}>
          {isVideo ? (
            renderVideoCover(mergedCover, () => handleImageClick(0))
          ) : (
            <img
              src={imageUrl}
              loading="lazy"
              decoding="async"
              alt="封面"
              className={styles.postImage}
              onClick={() => handleImageClick(0)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
        </div>
      </div>
    );
  };

  // 渲染可滑动图片列表（用于非2:1比例的封面图或没有封面图的情况）
  const renderImageGrid = () => {
    const showCoverInGrid = mergedCover && !isCover2to1();
    
    // 构建所有图片列表，确保每个图片都有完整的字段
    let allImages: any[] = [];
    
    if (showCoverInGrid && mergedCover) {
      // 添加封面到网格中
      allImages.push({
        id: mergedCover.id,
        url: mergedCover.url,
        webpUrl: mergedCover.webpUrl,
        fileWidth: mergedCover.fileWidth,
        fileHeight: mergedCover.fileHeight,
        width: mergedCover.width,
        height: mergedCover.height,
        thumbUrl: mergedCover.thumbUrl || mergedCover.url
      });
    }
    
    // 添加 imageList 中的所有图片（保持原始数据，包含 webpUrl）
    allImages = [...allImages, ...imageList];
    
    if (allImages.length === 0) {
      return null;
    }

    const aspectRatioClass = getAspectRatioClass();
    const displayImages = allImages.slice(0, maxDisplay);
    const hasMore = allImages.length > maxDisplay;

    return (
      <div className={classNames(styles.imageGrid, styles[aspectRatioClass])}>
        <div className={styles.imagesContainer}>
          {displayImages.map((image, index) => {
            const isLastItem = index === maxDisplay - 1 && hasMore;
            const imageUrl = getImageUrl(image);
            
            return (
              <div 
                key={image.id || index} 
                className={classNames(
                  styles.imageItem, 
                  isLastItem ? styles.lastItem : ''
                )}
              >
                {!isLastItem && (
                  <div className={styles.imageWrapper}>
                    {showCoverInGrid && index === 0 && isVideo ? (
                      renderVideoCover(mergedCover, () => handleImageClick(index))
                    ) : (
                      <>
                        <img
                          src={imageUrl}
                          loading="lazy"
                          decoding="async"
                          className={styles.postImage}
                          onClick={() => handleImageClick(index)}
                          onError={(e) => {
                            handleImageError(e, aspectRatioClass == 'aspect-1-1' ? 1 : 2)
                          }}
                        />
                        {(image as any).isLive && (
                          <img src={livePhotoImage.liveIcon} alt="live" className={styles.liveIcon} />
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {isLastItem && (
                  <div 
                    className={styles.moreOverlay} 
                    onClick={() => handleImageClick(index)}
                  >
                    <span className={styles.moreLabel}>查<br/>看<br/>全<br/>部<br/>{allImages.length}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 主渲染逻辑
  const singleCover = renderSingleCover();
  if (singleCover) {
    return (
      <div className={styles.postImages}>
        {singleCover}
      </div>
    );
  }
  
  const imageGrid = renderImageGrid();
  return (
    <div className={styles.postImages}>
      {imageGrid}
    </div>
  );
};

export default PCThreadImgList;
